import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  EXPEDIENTE_SECCIONES,
  etiquetaDocumentoExpediente,
  etiquetaRetieTipo,
  type ExpedienteSeccion,
} from "../../config/expedienteDocumentalSections";

type DocEstado = { estado: string; observacion?: string };

type Props = {
  documentosPorTipo: Record<string, { id?: number; created_at?: string; nombre_archivo?: string } | undefined>;
  retieTiposRequeridos: string[];
  informesAvanceObra?: Array<{ tipo: string; label: string }>;
  getEstadoDocumento: (tipo: string) => DocEstado;
  onVisualizar: (tipo: string) => void;
  onDescargar: (tipo: string) => void;
  guardando: boolean;
  totalDocumentosCargados: number;
};

function chipColor(estado: string): "default" | "success" | "warning" | "error" | "info" {
  const e = estado.toLowerCase();
  if (e === "aprobado" || e === "generado" || e === "completado") return "success";
  if (e === "desaprobado" || e === "rechazado" || e === "devuelto") return "error";
  if (e === "pendiente" || e === "pendiente revision" || e === "cargado") return "warning";
  if (e === "opcional") return "info";
  return "default";
}

function FilaDocumento({
  tipo,
  label,
  doc,
  estado,
  observacion,
  guardando,
  onVisualizar,
  onDescargar,
}: {
  tipo: string;
  label: string;
  doc?: { id?: number; created_at?: string; nombre_archivo?: string };
  estado: string;
  observacion?: string;
  guardando: boolean;
  onVisualizar: (tipo: string) => void;
  onDescargar: (tipo: string) => void;
}) {
  const cargado = Boolean(doc?.id);
  return (
    <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, opacity: cargado ? 1 : 0.85 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={5}>
          <Typography fontWeight={600} variant="body2">
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {tipo}
          </Typography>
          {cargado && doc?.nombre_archivo && (
            <Typography variant="caption" color="text.secondary" display="block">
              Archivo: {doc.nombre_archivo}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          <Chip size="small" label={estado} color={chipColor(estado)} />
          {observacion ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Obs.: {observacion}
            </Typography>
          ) : null}
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              disabled={!cargado || guardando}
              onClick={() => onVisualizar(tipo)}
            >
              Visualizar
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!cargado || guardando}
              onClick={() => onDescargar(tipo)}
            >
              Descargar
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export default function ExpedienteDocumentalPanel({
  documentosPorTipo,
  retieTiposRequeridos,
  informesAvanceObra = [],
  getEstadoDocumento,
  onVisualizar,
  onDescargar,
  guardando,
  totalDocumentosCargados,
}: Props) {
  const tiposCatalogados = React.useMemo(() => {
    const set = new Set<string>();
    for (const sec of EXPEDIENTE_SECCIONES) {
      for (const d of sec.documentos) set.add(d.tipo);
    }
    for (const t of retieTiposRequeridos) set.add(t);
    for (const inf of informesAvanceObra) set.add(inf.tipo);
    return set;
  }, [retieTiposRequeridos, informesAvanceObra]);

  const seccionesConInformes = React.useMemo((): ExpedienteSeccion[] => {
    if (!informesAvanceObra.length) return EXPEDIENTE_SECCIONES;
    const extra: ExpedienteSeccion = {
      id: "informes_avance_obra",
      titulo: "Informes avance de obra (mantenimiento)",
      documentos: informesAvanceObra,
    };
    const idx = EXPEDIENTE_SECCIONES.findIndex((s) => s.id === "ejecucion");
    if (idx < 0) return [...EXPEDIENTE_SECCIONES, extra];
    const copy = [...EXPEDIENTE_SECCIONES];
    copy.splice(idx, 0, extra);
    return copy;
  }, [informesAvanceObra]);

  const documentosAdicionales = React.useMemo(() => {
    return Object.keys(documentosPorTipo)
      .filter((t) => documentosPorTipo[t]?.id && !tiposCatalogados.has(t))
      .sort();
  }, [documentosPorTipo, tiposCatalogados]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Expediente del proyecto — <strong>solo visualización</strong>. Se actualiza automáticamente al cargar o
        validar documentos en cualquier etapa. Total en archivo: <strong>{totalDocumentosCargados}</strong>{" "}
        documento(s).
      </Alert>

      {seccionesConInformes.map((seccion, idx) => {
        const docsSeccion = seccion.retieDinamico
          ? retieTiposRequeridos.map((t) => ({ tipo: t, label: etiquetaRetieTipo(t) }))
          : seccion.documentos;

        const cargadosSeccion = docsSeccion.filter((d) => documentosPorTipo[d.tipo]?.id).length;

        return (
          <Accordion key={seccion.id} defaultExpanded={idx < 3} disableGutters sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography fontWeight={700}>{seccion.titulo}</Typography>
                <Chip
                  size="small"
                  label={`${cargadosSeccion} / ${docsSeccion.length || "—"} cargados`}
                  color={cargadosSeccion > 0 ? "primary" : "default"}
                  variant="outlined"
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {seccion.retieDinamico && docsSeccion.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no hay equipos RETIE declarados o asignados para este proyecto.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {docsSeccion.map((docRef) => {
                    const doc = documentosPorTipo[docRef.tipo];
                    const { estado, observacion } = getEstadoDocumento(docRef.tipo);
                    return (
                      <FilaDocumento
                        key={docRef.tipo}
                        tipo={docRef.tipo}
                        label={docRef.label}
                        doc={doc}
                        estado={doc?.id ? estado : "Sin cargar"}
                        observacion={observacion}
                        guardando={guardando}
                        onVisualizar={onVisualizar}
                        onDescargar={onDescargar}
                      />
                    );
                  })}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {documentosAdicionales.length > 0 && (
        <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Otros documentos del expediente</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {documentosAdicionales.map((tipo) => {
                const doc = documentosPorTipo[tipo];
                const { estado, observacion } = getEstadoDocumento(tipo);
                return (
                  <FilaDocumento
                    key={tipo}
                    tipo={tipo}
                    label={etiquetaDocumentoExpediente(tipo)}
                    doc={doc}
                    estado={estado}
                    observacion={observacion}
                    guardando={guardando}
                    onVisualizar={onVisualizar}
                    onDescargar={onDescargar}
                  />
                );
              })}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
